import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { Link as RouterLink, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useAdminUserQuery } from '../../api/admin';
import { AppCard, AppPageHeader } from '../../components';
import { PANEL_SX } from '../../components/Admin/OverviewPanel';
import { DeactivateUserDialog, UserOverviewTab, UserProfileHeader, UserViewsTable, UserWorksheetsTable, UserWatchlistsTable, UserDashboardTable, UserEmailReportsTable, UserDatasetPermissionsTable, UserScreenersTable, UserSyncSettingsTable } from '../../components/Admin';

type TabValue = 'overview' | 'dataset-permissions' | 'views' | 'worksheets' | 'watchlists' | 'screeners' | 'dashboard' | 'email-reports' | 'sync-settings';
const VALID_TABS: TabValue[] = ['overview', 'dataset-permissions', 'views', 'worksheets', 'watchlists', 'screeners', 'dashboard', 'email-reports', 'sync-settings'];

export default function UserActivityPage() {
	const { user_id } = useParams<{ user_id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const userId = parseInt(user_id!, 10);
	const [deactivateOpen, setDeactivateOpen] = useState(false);

	// Router state gives an instant title when navigating within the app; the
	// query is the source of truth (a fresh tab has no router state).
	const { userEmail: stateEmail, userName: stateName } =
		(location.state as { userEmail?: string; userName?: string }) ?? {};
	const { data: user } = useAdminUserQuery(userId);
	const userName = user?.full_name ?? stateName;
	const userEmail = user?.email ?? stateEmail;
	const userLabel = userName || userEmail || `User #${userId}`;

	const teams = user?.teams ?? [];
	const organizations = teams.filter(
		(t, i) => teams.findIndex((o) => o.organization_id === t.organization_id) === i,
	);

	const rawTab = searchParams.get('tab') as TabValue | null;
	const tab: TabValue = rawTab && VALID_TABS.includes(rawTab) ? rawTab : 'overview';

	const handleTabChange = (_: React.SyntheticEvent, value: TabValue) => {
		setSearchParams({ tab: value }, { replace: true, state: location.state });
	};

	return (
		<Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
			<AppPageHeader>
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mb={1}>
					<Button
						size="small"
						startIcon={<ArrowBackIcon />}
						onClick={() => navigate('/admin/users')}
					>
						All Users
					</Button>
					{/* Shown to every admin; the backend's deactivate_users check answers in the dialog. */}
					<Button
						size="small"
						color="error"
						variant="outlined"
						startIcon={<PersonOffIcon />}
						onClick={() => setDeactivateOpen(true)}
						disabled={!user}
					>
						Deactivate
					</Button>
				</Stack>
				{/* One header card: the name block, a divider, then User Info, so it is there on every tab. */}
				<Paper elevation={0} sx={{ ...PANEL_SX, display: 'flex', flexWrap: 'wrap', gap: 3, p: 2 }}>
					<Box
						sx={{
							flexShrink: 0,
							pr: { xs: 0, md: 3 },
							pb: { xs: 1.5, md: 0 },
							borderRight: { xs: 0, md: '1px solid rgba(56, 56, 56, 1)' },
							borderBottom: { xs: '1px solid rgba(56, 56, 56, 1)', md: 0 },
							width: { xs: '100%', md: 'auto' },
						}}
					>
						<Typography variant="h5">{userLabel}</Typography>
						{userName && userEmail && (
							<Typography variant="body2" color="text.secondary">{userEmail}</Typography>
						)}
						{user && (
							<Typography variant="body2" color="text.secondary">
								Team:{' '}
								{teams.length > 0
									? teams.map((t, i) => (
										<React.Fragment key={t.id}>
											{i > 0 && ', '}
											<Link component={RouterLink} to={`/admin/teams/${t.id}`}>{t.name}</Link>
										</React.Fragment>
									))
									: null}
								{' | '}
								Organization:{' '}
								{organizations.length > 0
									? organizations.map((t, i) => (
										<React.Fragment key={t.organization_id}>
											{i > 0 && ', '}
											<Link component={RouterLink} to={`/admin/organizations/${t.organization_id}`}>
												{t.organization_name}
											</Link>
										</React.Fragment>
									))
									: null}
							</Typography>
						)}
					</Box>
					<Box sx={{ flex: 1, minWidth: 320 }}>
						<UserProfileHeader userId={userId} />
					</Box>
				</Paper>
			</AppPageHeader>
			{/* Tabs sit on the page background. The Overview tab lays its own cards out
			    on that background; every other tab keeps the single card it always had. */}
			<Box sx={{ mx: '40px', mt: '30px', mb: '20px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
				<Tabs
					value={tab}
					onChange={handleTabChange}
					sx={{ borderBottom: 1, borderColor: 'divider', mb: 2.5 }}
				>
					<Tab label="Overview" value="overview" />
					<Tab label="Dataset Permissions" value="dataset-permissions" />
					<Tab label="Views" value="views" />
					<Tab label="Worksheets" value="worksheets" />
					<Tab label="Watchlists" value="watchlists" />				<Tab label="Screeners" value="screeners" />					<Tab label="Dashboard" value="dashboard" />
					<Tab label="Email Reports" value="email-reports" />
					<Tab label="Sync Resources" value="sync-settings" />
				</Tabs>
				{tab === 'overview' && (
					<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
						<UserOverviewTab userId={userId} />
					</Box>
				)}
				{tab !== 'overview' && (
					<AppCard sx={{ m: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
						{tab === 'dataset-permissions' && <UserDatasetPermissionsTable fill userId={userId} userEmail={userEmail ?? `user-${userId}`} />}
						{tab === 'views' && <UserViewsTable fill userId={userId} />}
						{tab === 'worksheets' && <UserWorksheetsTable fill userId={userId} />}
						{tab === 'watchlists' && <UserWatchlistsTable fill userId={userId} />}			{tab === 'screeners' && <UserScreenersTable fill userId={userId} />}				{tab === 'dashboard' && <UserDashboardTable fill userId={userId} />}
						{tab === 'email-reports' && <UserEmailReportsTable fill userId={userId} />}
						{tab === 'sync-settings' && <UserSyncSettingsTable fill userId={userId} />}
					</AppCard>
				)}
			</Box>
			<DeactivateUserDialog
				user={deactivateOpen ? user ?? null : null}
				onClose={() => setDeactivateOpen(false)}
				onDeactivated={() => navigate('/admin/users')}
			/>
		</Box>
	);
}
